/**
 * SlackAlertCard - system_admin control for the Slack ticket-alerts
 * webhook URL.
 *
 * Backs ``slack.new_ticket_alert`` in system_settings (see
 * backend/app/services/slack_notification_service.py). A non-empty
 * URL enables three alert flows:
 *
 *   1. New ticket created -> message to channel.
 *   2. Ticket assigned to Niki or Greg -> message addressed to them.
 *   3. Response / note added to a Niki- or Greg-assigned ticket ->
 *      message to channel (skips self-pings).
 *
 * Urgent-priority tickets get @-mentions appended so Slack actually
 * pushes a notification. Empty URL = feature off.
 *
 * Naming note: Greg picked the label "New Ticket Slack Alert" even
 * though the row drives all three trigger types - the most prominent
 * trigger is the new-ticket one. The card description below makes
 * the full scope explicit so admins aren't surprised.
 */
import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";

import { Alert, Button, Card, Input } from "@/components/ui";
import api from "@/lib/api";
import type { SlackAlertsPayload } from "@/types/api";

const QUERY_KEY = ["admin", "slack-alerts"] as const;

// Mirror of the backend's _SLACK_WEBHOOK_PREFIX validator. Client-
// side validation gives an immediate hint; the server still
// enforces it on PUT so a CLI / API caller bypassing this UI can't
// store a non-Slack URL.
const SLACK_WEBHOOK_PREFIX = "https://hooks.slack.com/";

export function SlackAlertCard(): JSX.Element {
  const queryClient = useQueryClient();

  const query = useQuery<SlackAlertsPayload>({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      const r = await api.get<SlackAlertsPayload>("/admin/slack-alerts");
      return r.data;
    },
  });

  const [webhookUrl, setWebhookUrl] = useState<string>("");
  const [validationError, setValidationError] = useState<string | null>(null);
  const [savedRecently, setSavedRecently] = useState<boolean>(false);

  // Seed local form state only when the server data is structurally
  // different from what we last seeded - same guard as the email
  // send-rules card so an in-progress edit isn't clobbered by a
  // background refetch.
  const lastSeededRef = useRef<string | null>(null);
  useEffect(() => {
    if (!query.data) return;
    const sig = query.data.webhook_url ?? "";
    if (sig === lastSeededRef.current) return;
    lastSeededRef.current = sig;
    setWebhookUrl(query.data.webhook_url ?? "");
  }, [query.data]);

  const saver = useMutation({
    mutationFn: async (body: { webhook_url: string | null }) => {
      const r = await api.put<SlackAlertsPayload>("/admin/slack-alerts", body);
      return r.data;
    },
    onSuccess: (saved) => {
      queryClient.setQueryData(QUERY_KEY, saved);
      setSavedRecently(true);
      window.setTimeout(() => setSavedRecently(false), 1500);
    },
  });

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setValidationError(null);
    const trimmed = webhookUrl.trim();
    if (trimmed && !trimmed.startsWith(SLACK_WEBHOOK_PREFIX)) {
      setValidationError(
        `Webhook URL must start with '${SLACK_WEBHOOK_PREFIX}' or be empty.`,
      );
      return;
    }
    // Empty -> null clears the row; non-empty saves the URL.
    saver.mutate({ webhook_url: trimmed || null });
  }

  const current = query.data?.webhook_url ?? "";
  const dirty = !!query.data && webhookUrl.trim() !== current;
  const configured = !!current;

  return (
    <Card padding="md" data-testid="slack-alert-card">
      <h3 className="text-base font-semibold text-neutral-900">
        New Ticket Slack Alert
      </h3>
      <p className="mt-1 text-sm text-neutral-600">
        Posts a message to a Slack channel via incoming webhook on three
        events: a new ticket is created, a ticket is assigned to Niki
        or Greg, or someone responds to a Niki- or Greg-assigned ticket.
        Urgent-priority tickets also @-mention Niki and Greg so Slack
        pushes a notification. Leave empty to disable.
      </p>

      <form onSubmit={handleSubmit} className="mt-4 space-y-4">
        <Input
          id="slack-webhook-url"
          type="url"
          label="Slack incoming webhook URL"
          placeholder={SLACK_WEBHOOK_PREFIX + "services/..."}
          helperText={
            configured
              ? "Currently enabled. Clear the field and Save to disable."
              : "Currently disabled. Paste a Slack webhook URL and Save to enable."
          }
          value={webhookUrl}
          onChange={(e) => setWebhookUrl(e.target.value)}
          disabled={query.isLoading}
          autoComplete="off"
          data-testid="slack-alert-input"
        />

        {validationError && <Alert variant="error">{validationError}</Alert>}
        {saver.isError && (
          <Alert variant="error">
            Save failed: {(saver.error as Error)?.message ?? "unknown error"}
          </Alert>
        )}

        <div className="flex items-center gap-3">
          <span
            data-just-saved={savedRecently ? "true" : "false"}
            className="inline-block"
          >
            <Button
              type="submit"
              variant="primary"
              disabled={!dirty || saver.isPending}
              isLoading={saver.isPending}
              data-testid="slack-alert-save"
            >
              Save
            </Button>
          </span>
          {savedRecently && (
            <span
              className="text-sm text-emerald-700"
              data-testid="slack-alert-saved"
            >
              Saved.
            </span>
          )}
        </div>
      </form>
    </Card>
  );
}

export default SlackAlertCard;
